<template>
  <div
    class="tag tw:inline-flex tw:w-fit tw:max-w-full tw:items-center tw:gap-2 tw:whitespace-nowrap tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary"
    :class="[
      `tag--${props.color}`,
      {
        'tw:h-[24px] tw:px-2.5 tw:text-sm': size === 'md',
        'tw:py-1 tw:pl-2 tw:pr-0.5 tw:text-sm': size === 'sm',
        'tw:h-[32px] tw:px-4': size === 'lg',
        'tw:cursor-pointer': !hideCursor,
        'tw:rounded-full': rounded,
        'tw:rounded': !rounded,
        'tag-hover': !hideHover,
      },
    ]"
    :role="!hideCursor ? 'button' : undefined"
    :style="tagStyles"
    :tabindex="!hideCursor ? 0 : undefined"
    @click="$emit('click', $event)"
    @keydown.enter="!hideCursor && $emit('click', $event)"
    @keydown.space.prevent="!hideCursor && $emit('click', $event)"
  >
    <BaseIcon
      v-if="icon"
      :icon="icon"
    />

    <slot>
      <BaseEllipseContent class="tag-label">
        {{ label }}
      </BaseEllipseContent>
    </slot>

    <slot name="close-button">
      <BaseButton
        v-if="!hideRemoveButton"
        :aria-label="removeButtonAriaLabel"
        :circle="rounded"
        class="tag-icon tw:pointer-events-auto tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center"
        :class="{
          'tw:-mr-1.5': size === 'md',
          'tw:-mr-2.5': size === 'lg',
        }"
        flat
        new-design
        plain
        @click.stop="$emit('remove')"
      >
        <BaseIcon
          aria-hidden="true"
          icon="close"
        />
      </BaseButton>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { type DesignSystemIcons } from "design-system/types";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseButton from "shared/components/base/BaseButton";
import BaseEllipseContent from "shared/components/base/BaseEllipseContent";
import BaseIcon from "shared/components/base/BaseIcon";
import { defaultTagColor, tagPalette } from "shared/constants";
import { MediumField } from "shared/helpers/media";

enum Colors {
  DENIM = "denim",
  NONE = "none",
  PRIMARY = "primary",
  SECONDARY = "secondary",
  SELECTED = "selected",
}

interface ColorPallete {
  fontColor: string;
}

interface ColorPalletes {
  [key: string]: ColorPallete;
}

export interface Props {
  color?: Colors | MediumField | string;
  hideCursor?: boolean;
  hideHover?: boolean;
  hideRemoveButton?: boolean;
  icon?: DesignSystemIcons;
  label?: string | number;
  palette?: ColorPalletes;
  removeAriaLabel?: string;
  rounded?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  color: defaultTagColor,
  icon: undefined,
  palette: () => tagPalette,
  removeAriaLabel: undefined,
  size: "md",
});

defineEmits(["click", "remove"]);

const removeButtonAriaLabel = computed<string>(() => {
  if (props.removeAriaLabel) return props.removeAriaLabel;

  if (props.label) {
    return getLocaleText("tag.remove_label", { label: props.label });
  }

  return getLocaleText("global.remove");
});

const colorPalette = computed<ColorPalletes>(() => props.palette);

const tagColor = computed(() => {
  if (Object.values(Colors).includes(props.color as Colors)) {
    return props.color;
  }

  if (Object.values(MediumField).includes(props.color as MediumField)) {
    return props.color;
  }

  return null;
});

const tagStyles = computed(() => {
  if (tagColor.value) {
    return {};
  }

  const fontColor = colorPalette.value[props.color]?.fontColor || "white";

  return {
    "--tag-text-color": fontColor,
    "background-color": props.color || defaultTagColor,
    color: fontColor,
  };
});
</script>

<style lang="scss">
.tag {
  --tag-bg-color: none;
  --tag-border-color: transparent;
  --tag-hover-bg-color: var(--s-color-denim-1);
  --tag-text-color: var(--s-button-color-secondary-default);
  --tag-button-hover-bg-color: none;

  color: var(--tag-text-color);
  background-color: var(--tag-bg-color);
  border: 1px solid var(--tag-border-color);

  &.tag--denim {
    --tag-bg-color: var(--s-color-denim-1);
    --tag-border-color: var(--s-color-denim-3);
    --tag-hover-bg-color: var(--s-color-denim-2);
    --tag-text-color: var(--s-color-blue-9);
    --tag-button-hover-bg-color: var(--s-color-denim-3);
  }

  &.tag--secondary {
    --tag-bg-color: var(--s-button-bg-secondary-default);
    --tag-hover-bg-color: var(--s-button-bg-secondary-hover);
    --tag-text-color: var(--s-button-color-secondary-default);
    --tag-button-hover-bg-color: var(--s-button-bg-secondary-active);
  }

  &.tag--primary {
    --tag-bg-color: var(--s-button-bg-primary-default);
    --tag-hover-bg-color: var(--s-button-bg-primary-hover);
    --tag-button-hover-bg-color: var(--s-button-bg-primary-active);
    --tag-text-color: var(--s-button-color-primary-default);
  }

  &.tag--danger {
    --tag-bg-color: var(--s-button-bg-danger-default);
    --tag-hover-bg-color: var(--s-button-bg-danger-hover);
    --tag-button-hover-bg-color: var(--s-button-bg-danger-active);
    --tag-text-color: var(--s-button-color-primary-default);
  }

  &.tag--selected {
    --tag-bg-color: var(--s-color-blue-1);
    --tag-hover-bg-color: var(--s-color-blue-2);
    --tag-text-color: var(--s-color-blue-7);
    --tag-button-hover-bg-color: var(--s-color-blue-3);
  }

  @each $medium in $media {
    &.tag--#{$medium} {
      --tag-bg-color: var(--s-button-bg-#{$medium}-default);
      --tag-hover-bg-color: var(--s-button-bg-#{$medium}-hover);
      --tag-button-hover-bg-color: var(--s-button-bg-#{$medium}-active);
      --tag-text-color: var(--s-button-color-#{$medium}-default);
    }
  }

  .tag-icon.new-design.base-button {
    --c-button-color: var(--tag-text-color);
    --c-button-color-hover: var(--tag-text-color);
    --c-button-bg-default: transparent;
    --c-button-bg-hover: var(--tag-button-hover-bg-color);
    --c-button-bg-active: var(--tag-button-hover-bg-color);
    --c-button-border-default: transparent;
    --c-button-border-hover: transparent;
    --c-button-border-active: transparent;
    --c-button-padding-inline: 0;
    --c-button-padding-block: 0;

    min-height: 0;
  }

  .tag-hover {
    &:hover {
      --tag-bg-color: var(--tag-hover-bg-color);
    }
  }
}
</style>
